/**
 * Traspasos — pantalla unificada de traspaso de matrículas.
 *
 * La vista principal es el listado de traspasos vigentes embebido
 * (cancelartraspaso, ares6060: búsqueda por cédula o fechas, anulación e
 * impresión del comprobante). Tarjetas de acción:
 *   - "Traspasar matrícula" (ares6030): el formulario transaccional en
 *     diálogo a pantalla completa; al guardar, el listado se recarga.
 *   - "Informe de traspasos" (programa traspaso, antes en Inventario): el
 *     reporte con export PDF, en diálogo con barra Volver.
 *
 * Sustituye las entradas de menú de traspasarmatricula.js,
 * cancelartraspaso.js y motores/informetraspaso.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import TraspasarMatricula from "modules/matriculas/traspasarmatricula";
import CancelarTraspaso from "modules/matriculas/cancelartraspaso";
import InformeTraspaso from "modules/motores/informetraspaso";
import useAccionInicial from "lib/useAccionInicial";

export default function Traspasos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeTraspasar = tienePermisosModulo(roleDetalles, "ares6030");
  const puedeAnular = tienePermisosModulo(roleDetalles, "ares6060");
  const puedeInforme = tienePermisosModulo(roleDetalles, "traspaso");

  const [traspasoOpen, setTraspasoOpen] = useState(false);
  const [informeOpen, setInformeOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    traspasar: { permitido: puedeTraspasar, abrir: () => setTraspasoOpen(true) },
    informe: { permitido: puedeInforme, abrir: () => setInformeOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras registrar un traspaso).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Traspasos"
        subtitle="Traspasa matrículas, consulta y anula traspasos en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeTraspasar || puedeInforme) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeTraspasar && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={SwapHorizOutlinedIcon}
                  titulo="Traspasar matrícula"
                  descripcion="Traspasa el vehículo a otro titular (la deuda activa pasa al nuevo dueño)"
                  color={FP.color.accentBlue}
                  onClick={() => setTraspasoOpen(true)}
                />
              </Grid>
            )}
            {puedeInforme && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Informe de traspasos"
                  descripcion="Traspasos por fechas y sucursal, con export a PDF"
                  color={FP.color.info}
                  onClick={() => setInformeOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: traspasos vigentes con anular/imprimir por fila */}
        {puedeAnular && <CancelarTraspaso embedded refreshToken={refreshKey} />}
      </Box>

      {/* Traspasar matrícula (formulario transaccional a pantalla completa) */}
      <Dialog fullScreen open={traspasoOpen} onClose={() => setTraspasoOpen(false)}>
        <TraspasarMatricula embedded onExit={() => { setTraspasoOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Informe de traspasos (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={informeOpen} onClose={() => setInformeOpen(false)}>
        <BarraVolver onExit={() => setInformeOpen(false)} />
        <InformeTraspaso />
      </Dialog>
    </Box>
  );
}
